import {makeLogger} from '../../../logger/logger';

const {
  debug,
  //info,
  //warn,
  err
} = makeLogger({
  pathToCurrentFile: 'utils/libs/dataflux/fluxProxy.ts',
});


export type ObservableChange<T = unknown> = {
  type: 'add' | 'delete' | 'update' | 'get',
  /**
   * Child that's actually altered. Key is the property name, its value is the new value
   */
  target: {
    [key: string]: T
  },
  /**
   * Property name that's altered
   */
  property: string,
  /**
   * Value that's being set
   */
  newValue: T,
  oldValue: T | undefined,
  /**
   * Property path with dot notation ex foo.0.bar
   */
  currentPath: `$.${string}`,
  /**
   * property path with the JSON pointer syntax ex /foo/0/bar
   */
  jsonPointer: `${string}`,
  /**
   * Proxy of the target object, will trigger the subscribers
   */
  proxy: T
};


const proxies = new WeakSet();
const isProxy = (obj: object) => proxies.has(obj);

const proxyHandler = (
  callbacks: Callbacks,
  level: number,
  prefix: `$${string}`,
  pointer: `${string}`,
) => {
    return {
      get: (target: {[key:string]:unknown}, key: string, receiver: unknown) => {
          if (key == '__isProxy' || key == '__getTarget') {
            return target[key];
          }

          if (key === 'root') {
            //debug('returning root');
            return target;
          }

          const value = target[key];

          if(typeof key === 'symbol'){
            return value;
          }


          const report: ObservableChange = {
            type: 'get',
            target: target,
            property: key,
            newValue: value,
            oldValue: value,
            currentPath: `${prefix as '$.{string}'}.${key}`,
            jsonPointer: `${pointer as `/${string}`}/${key}`,
            proxy: receiver
          };

          // NOTE: debug statemets are commented out to avoid clutter.

          //debug(`internalEventHandlers lvl ${level}, get executed for ${key} via ${report.currentPath}`, report);
          //@formatter:off
          callbacks.onGet?.forEach(cb => {try{cb(report);}catch(e){err(e);}});
          //@formatter:on

          if (isProxy(value as object)) {
            //debug('returning raw object because it\'s already a proxy', {target, prefix, key, value});
            return value;
          }
          //for Objects we return a nested proxy
          if (value instanceof Object) {
            //debug(`returning instance of new proxy ${key} in ${prefix}.${key}`)
            return new Proxy(value, proxyHandler(callbacks, level + 1, `${prefix}.${key}`, `${pointer}/${key}`));
          }
          //For methods, this means you have to redirect the method's this value to the original object as well:
          if (value instanceof Function) {
            //debug(`returning instance of function ${key} in ${prefix}.${key}`)
            //any, because we don't know what are the arguments, lol :)
            return function (...args: any[]) { //eslint-disable-line @typescript-eslint/no-explicit-any
                return value.apply(this === receiver ? target : this, args);
            };
          }
          //debug(`returning just value ${key} in ${prefix}.${key}`, value);
          return value;
      },
      set: (target: {[key:string]:unknown}, key: string, value: unknown) => {


          const report: ObservableChange = {
            type: !target.hasOwnProperty(key) ? 'add' : 'update',
            target: target,
            property: key,
            newValue: value,
            oldValue: target[key],
            currentPath: `${prefix as '$.{string}'}.${key}`,
            jsonPointer: `${pointer as `/${string}`}/${key}`,
            proxy: target[key],
          };

          //debug(`internalEventHandlers lvl ${level}, *set* executed for ${key} via ${report.type}`, report, value);

          if (report.type === 'add') {
            //@formatter:off
            callbacks.onAdd?.forEach(cb => {try{cb(report);}catch(e){err(e);}});
            //@formatter:on
          } else if(report.type === 'update') {
            //@formatter:off
            callbacks.onUpdate?.forEach(cb => {try{cb(report);}catch(e){err(e);}});
            //@formatter:on
          }else{
            throw new Error(`Unhandled fluxProxy report state: ${report.type}`);
          }

          target[key] = value;
          return true;
      },
      deleteProperty(target, key): boolean {
          if (key in target) {
            const report: ObservableChange = {
              type: 'delete',
              target: target,
              property: key,
              newValue: undefined,
              oldValue: target[key],
              currentPath: `${prefix as '$.{string}'}.${key}`,
              jsonPointer: `${pointer as `/${string}`}/${key}`,
              proxy: target[key],
            };
            debug(`internalEventHandlers lvl ${level}, *delete* executed for ${key} via ${report.type}`, report);
            delete target[key];
            //@formatter:off
            callbacks.onDelete?.forEach(cb => {try{cb(report);}catch(e){err(e);}});
            //@formatter:on
            return true;
          } else {
            return false;
          }
      },
    };
};

export interface Callbacks {
  onAdd?: Array<(change: ObservableChange) => void>;
  onDelete?: Array<(change: ObservableChange) => void>;
  onUpdate?: Array<(change: ObservableChange) => void>;
  onGet?: Array<(change: ObservableChange) => void>;
}


export const makeObservableProxy = <T>(proxiedObject: T, callbacks: Callbacks) => {

    return new Proxy(proxiedObject, proxyHandler(callbacks, 0, '$', '')) as T;
};
